import { useEffect, useMemo, useRef, useState } from "react"
import cytoscape, { type Core, type ElementDefinition, type LayoutOptions } from "cytoscape"
import fcose from "cytoscape-fcose"
import type { GraphNode, GraphPayload } from "@/types/knowledgeGraph"
import { edgeLabel, nodeLabel } from "@/lib/knowledgeGraph"
import { DEFAULT_CONCEPT_KIND, nodeColor } from "@/lib/graphPalette"
import { useMediaQuery } from "@/hooks/useMediaQuery"

interface GraphCanvasProps {
  graph: GraphPayload
  selectedId: number | null
  onSelect: (node: GraphNode) => void
  onExpand: (node: GraphNode) => void
}

// Registered once at module load. Cytoscape throws on a second registration, which
// Vite's HMR would otherwise trigger on every edit to this file.
let layoutRegistered = false
function registerLayout(): void {
  if (layoutRegistered) return
  cytoscape.use(fcose)
  layoutRegistered = true
}

/**
 * fCoSE over plain cose: it settles a graph of this size in a fraction of the time
 * and, more importantly, keeps clusters apart instead of letting a hub's neighbours
 * pile into one blob.
 */
const LAYOUT: LayoutOptions = {
  name: "fcose",
  animate: false,
  randomize: false,
  quality: "default",
  nodeRepulsion: () => 12000,
  idealEdgeLength: () => 90,
  nodeSeparation: 90,
} as LayoutOptions

const ZOOM_STEP = 1.25
const NARROW_QUERY = "(max-width: 1023px)"

function toElements(graph: GraphPayload): ElementDefinition[] {
  const nodeIds = new Set(graph.nodes.map((node) => node.id))

  const nodes: ElementDefinition[] = graph.nodes.map((node) => ({
    data: {
      id: String(node.id),
      label: nodeLabel(node),
      color: nodeColor(node),
      kind: node.label === "Concept" ? (node.kind ?? DEFAULT_CONCEPT_KIND) : node.label.toLowerCase(),
      isRoot: node.root ? "true" : "false",
    },
  }))

  // A hop limit can leave an edge whose far endpoint was never collected; drawing
  // it would make Cytoscape invent a blank node.
  const edges: ElementDefinition[] = graph.edges
    .filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to))
    .map((edge) => ({
      data: {
        id: `e${edge.id}`,
        source: String(edge.from),
        target: String(edge.to),
        label: edgeLabel(edge.type),
        weight: edge.strength ?? 0.4,
      },
    }))

  return [...nodes, ...edges]
}

function applyViewportStyles(cy: Core, narrow: boolean): void {
  cy.style()
    .selector("node")
    .style({
      width: narrow ? 28 : 22,
      height: narrow ? 28 : 22,
      "font-size": narrow ? "12px" : "10px",
      "text-max-width": narrow ? "130px" : "110px",
    })
    .selector('node[isRoot = "true"]')
    .style({
      width: narrow ? 40 : 34,
      height: narrow ? 40 : 34,
      "font-size": narrow ? "13px" : "12px",
    })
    .selector('node[kind = "document"]')
    .style({
      width: narrow ? 22 : 18,
      height: narrow ? 18 : 14,
    })
    .update()
}

/**
 * Cytoscape canvas for a graph payload.
 *
 * The instance is created once and its elements are replaced on each graph change:
 * tearing down Cytoscape on every update loses pan and zoom, which makes following a
 * chain of expansions unusable.
 */
export function GraphCanvas({ graph, selectedId, onSelect, onExpand }: GraphCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const cyRef = useRef<Core | null>(null)
  const elements = useMemo(() => toElements(graph), [graph])
  const narrow = useMediaQuery(NARROW_QUERY)
  const [ready, setReady] = useState(false)

  // Kept in refs so the Cytoscape event handlers registered on mount always call the
  // current callbacks without re-binding.
  const onSelectRef = useRef(onSelect)
  const onExpandRef = useRef(onExpand)
  onSelectRef.current = onSelect
  onExpandRef.current = onExpand

  const nodesById = useMemo(() => {
    const map = new Map<number, GraphNode>()
    graph.nodes.forEach((node) => map.set(node.id, node))
    return map
  }, [graph.nodes])
  const nodesByIdRef = useRef(nodesById)
  nodesByIdRef.current = nodesById

  useEffect(() => {
    if (!containerRef.current) return
    registerLayout()

    const cy = cytoscape({
      container: containerRef.current,
      elements: [],
      style: [
        {
          selector: "node",
          style: {
            "background-color": "data(color)",
            label: "data(label)",
            color: "#e2e8f0",
            "font-size": "10px",
            "text-valign": "bottom",
            "text-margin-y": 6,
            "text-wrap": "ellipsis",
            "text-max-width": "110px",
            width: 22,
            height: 22,
            "border-width": 0,
          },
        },
        {
          selector: 'node[isRoot = "true"]',
          style: { width: 34, height: 34, "border-width": 3, "border-color": "#f8fafc", "font-size": "12px" },
        },
        {
          selector: "node.selected",
          style: { "border-width": 3, "border-color": "#fbbf24" },
        },
        {
          selector: 'node[kind = "document"]',
          style: { shape: "round-rectangle", width: 18, height: 14 },
        },
        {
          selector: "edge",
          style: {
            width: "mapData(weight, 0, 1, 1, 4)",
            "line-color": "#334155",
            "target-arrow-color": "#334155",
            "target-arrow-shape": "triangle",
            "curve-style": "bezier",
            "arrow-scale": 0.7,
            label: "data(label)",
            "font-size": "8px",
            color: "#64748b",
            "text-rotation": "autorotate",
            "text-background-color": "#0f172a",
            "text-background-opacity": 0.85,
            "text-background-padding": "2px",
          },
        },
      ],
      minZoom: 0.2,
      maxZoom: 3,
      wheelSensitivity: 0.2,
    })

    cy.on("tap", "node", (event) => {
      const node = nodesByIdRef.current.get(Number(event.target.id()))
      if (node) onSelectRef.current(node)
    })

    // Double-tap stays for desktop; taphold is the reliable expand gesture on touch.
    cy.on("dbltap", "node", (event) => {
      const node = nodesByIdRef.current.get(Number(event.target.id()))
      if (node) onExpandRef.current(node)
    })

    cy.on("taphold", "node", (event) => {
      const node = nodesByIdRef.current.get(Number(event.target.id()))
      if (node) onExpandRef.current(node)
    })

    cyRef.current = cy
    setReady(true)

    return () => {
      cy.destroy()
      cyRef.current = null
      setReady(false)
    }
  }, [])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return

    const previousCount = cy.elements().length
    cy.elements().remove()
    cy.add(elements)
    cy.layout(LAYOUT).run()

    // Only refit when the canvas was empty; refitting on every expansion fights the
    // user's own panning.
    if (previousCount === 0) cy.fit(undefined, 40)
  }, [elements])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return

    cy.nodes().removeClass("selected")
    if (selectedId !== null) cy.getElementById(String(selectedId)).addClass("selected")
  }, [selectedId, elements])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy || !ready) return
    applyViewportStyles(cy, narrow)
  }, [narrow, ready])

  // Drawers and orientation changes alter the container without a graph update;
  // Cytoscape needs an explicit resize or the viewport goes blank / misaligned.
  useEffect(() => {
    const cy = cyRef.current
    const container = containerRef.current
    if (!cy || !container || !ready) return

    const resize = () => {
      cy.resize()
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(container)
    window.addEventListener("resize", resize)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", resize)
    }
  }, [ready])

  const zoomBy = (factor: number) => {
    const cy = cyRef.current
    if (!cy) return
    cy.zoom({
      level: Math.min(cy.maxZoom(), Math.max(cy.minZoom(), cy.zoom() * factor)),
      renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    })
  }

  const fitGraph = () => {
    cyRef.current?.fit(undefined, 40)
  }

  return (
    <div className="relative h-full w-full">
      <div
        ref={containerRef}
        data-testid="knowledge-graph-canvas"
        className="h-full w-full touch-none bg-slate-950 lg:rounded-lg"
      />
      <div
        className="absolute bottom-3 right-3 flex flex-col gap-1.5"
        role="toolbar"
        aria-label="Graph zoom controls"
      >
        <button
          type="button"
          onClick={() => zoomBy(ZOOM_STEP)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-700 bg-slate-900/90 text-lg text-slate-200 shadow-sm hover:bg-slate-800"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => zoomBy(1 / ZOOM_STEP)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-700 bg-slate-900/90 text-lg text-slate-200 shadow-sm hover:bg-slate-800"
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          onClick={fitGraph}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-700 bg-slate-900/90 text-[10px] font-semibold uppercase tracking-wide text-slate-200 shadow-sm hover:bg-slate-800"
          aria-label="Fit graph"
        >
          Fit
        </button>
      </div>
    </div>
  )
}
